import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, MapPin, MousePointerClick, Flame } from "lucide-react";

const STORAGE_KEY = "canvassing-tutorial-dismissed-v1";

export function CanvassingTutorial() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && !localStorage.getItem(STORAGE_KEY)) {
      setOpen(true);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <Card className="max-w-md w-full p-5 relative">
        <Button variant="ghost" size="icon" onClick={dismiss} className="absolute top-2 right-2 h-7 w-7">
          <X className="w-4 h-4" />
        </Button>
        <h3 className="text-lg font-bold mb-1">How canvassing works</h3>
        <p className="text-xs text-muted-foreground mb-4">3 steps. You'll never knock blind again.</p>

        <div className="space-y-3">
          <Step
            n={1}
            icon={MousePointerClick}
            title="Tap a house on the map"
            body="Bridgette enriches it with sq ft, owner intel, equity, and storm history — then scores it 0-100."
          />
          <Step
            n={2}
            icon={Flame}
            title="Knock the hot ones"
            body="🟢 75+ = high equity + recent storm. 🟡 warm. 🔴 skip. Top 25 ranked in the drawer."
          />
          <Step
            n={3}
            icon={MapPin}
            title="Log the door"
            body="Tap '+ Log Door' or pick an outcome — appointments auto-create leads, not-homes auto-schedule re-knocks."
          />
        </div>

        <Button onClick={dismiss} className="w-full mt-5">Got it — let's knock</Button>
      </Card>
    </div>
  );
}

function Step({ n, icon: Icon, title, body }: { n: number; icon: any; title: string; body: string }) {
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shrink-0">{n}</div>
      <div>
        <div className="font-semibold text-sm flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {title}</div>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}
